'use client';

import { type MouseEventHandler } from 'react';
import { CircleCheck, Info } from 'lucide-react';
import { Tooltip, TriggerText } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import {
  getTotalMonthlyPriceTooltipText,
  requiresFeesDisclosure,
  shouldShowTotalMonthlyPriceIndicator,
  type TotalMonthlyPriceListing,
} from '@/src/lib/utils/totalMonthlyPrice';
import * as styles from './totalMonthlyPrice.styles';

const REVIEW_SENTENCE = /\s*Review the costs and fees breakdown for full details - additional fees may apply\./;

type FeesMode = 'legacy' | 'always' | 'never';

type IndicatorClassNames = {
  badge?: string;
  fees?: string;
  icon?: string;
  label?: string;
  popoverContent?: string;
  trigger?: string;
};

type TotalMonthlyPriceIndicatorProps = {
  listing: TotalMonthlyPriceListing | null | undefined;
  floorplanId?: string;
  modelIds?: Array<string | undefined>;
  feesMode?: FeesMode;
  className?: string;
  classNames?: IndicatorClassNames;
  testId?: string;
  feesTestId?: string;
  onTriggerClick?: MouseEventHandler;
  showLabel?: boolean;
};

type TotalMonthlyPriceNoticeProps = {
  listing: TotalMonthlyPriceListing | null | undefined;
  feesMode?: FeesMode;
  className?: string;
};

function shouldShowFees(listing: TotalMonthlyPriceListing | null | undefined, feesMode: FeesMode): boolean {
  if (feesMode === 'never') return false;
  if (feesMode === 'always') return true;
  return requiresFeesDisclosure(listing);
}

export function TotalMonthlyPriceIndicator({
  listing,
  floorplanId,
  modelIds,
  feesMode = 'legacy',
  className,
  classNames,
  testId,
  feesTestId,
  onTriggerClick,
  showLabel = true,
}: TotalMonthlyPriceIndicatorProps) {
  const t = useTranslations('listing');
  if (!listing) return null;

  if (listing.hasTotalMonthlyPrice === true) {
    const icon = <LucideIcon icon={CircleCheck} size="sm" className={cx(styles.totalPriceIcon, classNames?.icon)} />;
    const label = showLabel ? (
      <span className={cx(styles.totalPriceLabel, classNames?.label)}>{t('totalPrice')}</span>
    ) : null;

    const tooltipModelIds = modelIds?.length ? modelIds : [floorplanId];
    const rawTooltipText = getTotalMonthlyPriceTooltipText(listing.totalMonthlyPriceDisplayInfo, tooltipModelIds);
    const tooltipText = rawTooltipText?.replace(REVIEW_SENTENCE, '').trim();

    if (tooltipText) {
      return (
        <span className={cx(styles.totalPriceTriggerWrapper, className)}>
          {icon}
          <Tooltip.Root placement="bottom" shouldCloseOnViewportLeave>
            <Tooltip.Trigger>
              <TriggerText
                css={{ color: 'text.accent.brand.hero' }}
                className={classNames?.trigger}
                onClick={onTriggerClick}
                data-testid={testId}
                aria-label={`${t('totalPrice')}. ${tooltipText}`}
              >
                {label}
              </TriggerText>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content>
                {tooltipText}
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </span>
      );
    }

    return (
      <span className={cx(styles.totalPriceBadge, className, classNames?.badge)} data-testid={testId}>
        {icon}
        {label}
      </span>
    );
  }

  if (!shouldShowFees(listing, feesMode)) return null;

  return (
    <span className={cx(styles.feesApply, className, classNames?.fees)} data-testid={feesTestId}>
      {t('feesApply')}
    </span>
  );
}

export function TotalMonthlyPriceNotice({ listing, feesMode = 'legacy', className }: TotalMonthlyPriceNoticeProps) {
  const t = useTranslations('hdp');
  if (!shouldShowTotalMonthlyPriceIndicator(listing)) return null;

  const hasTotalMonthlyPrice = listing?.hasTotalMonthlyPrice === true;
  if (!hasTotalMonthlyPrice && !shouldShowFees(listing, feesMode)) return null;

  return (
    <p className={cx(styles.notice, className)}>
      <LucideIcon
        icon={hasTotalMonthlyPrice ? CircleCheck : Info}
        size="sm"
        className={styles.noticeIcon}
        color="currentColor"
      />
      {hasTotalMonthlyPrice ? t('totalMonthlyPriceNotice') : t('feesMayApplyNotice')}
    </p>
  );
}
